import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../lib/csrf"

/**
 * Which triage results the Reports index shows as columns (SMA-104).
 *
 * The columns are rendered by the server, so this only stores the choice and reloads; it never
 * touches the table. A burst of checkbox clicks settles into one write and one reload.
 *
 *   <div data-controller="column-picker" data-column-picker-url-value="/report_columns">
 */
export default class extends Controller {
  static targets = ["checkbox", "badge"]
  static values = { url: String, max: Number }

  connect() {
    this.limit()
  }

  disconnect() {
    clearTimeout(this.timer)
  }

  toggle() {
    this.limit()
    this.schedule(this.checked())
  }

  checked() {
    return this.checkboxTargets.filter((box) => box.checked).map((box) => box.value)
  }

  // The server caps the stored list, and posting a seventh would silently drop whichever column
  // sorts last in the DOM rather than the one just ticked. Stop it being tickable instead.
  limit() {
    const full = this.checked().length >= this.maxValue
    this.checkboxTargets.forEach((box) => { box.disabled = full && !box.checked })
  }

  reset(event) {
    event.preventDefault()
    this.schedule(null)
  }

  // Null means reset. The badge moves at once so the control feels answered, and the write waits
  // for the person to stop clicking.
  schedule(columns) {
    this.badgeTarget.textContent = columns ? columns.length : ""
    this.badgeTarget.classList.toggle("hidden", !columns || columns.length === 0)
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.save(columns), 400)
  }

  // Awaited, unlike the feature-intro dismissal, which detaches its own node and can fire and
  // forget: this reloads the page and would otherwise race its own write.
  async save(columns) {
    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken()
        },
        body: JSON.stringify(columns === null ? { reset: true } : { report_columns: columns })
      })
      if (!response.ok) return

      // The query survives; the page does not, since the columns are server-rendered. A result sort
      // on a column that is no longer shown is ignored by the controller, so it needs no scrubbing
      // here.
      const url = new URL(window.location.href)
      url.searchParams.delete("page")
      window.location.assign(url.toString())
    } catch (error) {
      console.error("Failed to store the chosen columns:", error)
    }
  }
}
